import React, { useState } from 'react';
import Button from '../../../components/Button';
import AddressAutocomplete, { PlaceData } from '../../../components/AddressAutocomplete';

interface AddProjectModalProps {
  onClose: () => void;
  onAdd: (projectData: { 
    name: string; 
    location: string; 
    coordinates?: { latitude: number; longitude: number };
    addressComponents?: PlaceData['address_components'];
    businessType?: string;
    locationType?: 'new' | 'existing';
  }) => void;
}

// Business type options based on the backend model
const BUSINESS_TYPE_OPTIONS = [
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'daycare', label: 'Daycare' },
  { value: 'printing_services', label: 'Printing Services' },
  { value: 'storage', label: 'Storage' },
  { value: 'retail', label: 'Retail' },
  { value: 'other', label: 'Other' }
];

const AddProjectModal: React.FC<AddProjectModalProps> = ({ onClose, onAdd }) => {
  const [step, setStep] = useState(1); // 1: Basic Info, 2: Location Details
  const [projectData, setProjectData] = useState({
    name: '',
    businessType: 'entertainment',
    locationType: 'new' as 'new' | 'existing',
    location: '',
    coordinates: undefined as { latitude: number; longitude: number } | undefined,
    addressComponents: undefined as PlaceData['address_components'] | undefined
  });
  const [errors, setErrors] = useState({
    name: '',
    location: ''
  });

  const handleAddressSelect = (place: PlaceData) => {
    console.log('Selected place:', place);
    
    // Determine display value based on business name availability
    let displayValue = place.formatted_address;
    if (place.name && projectData.locationType === 'existing') {
      displayValue = `${place.name} – ${place.formatted_address}`;
    }
    
    // Set project data with the selected place information
    setProjectData({
      ...projectData,
      location: displayValue,
      coordinates: {
        latitude: place.lat,
        longitude: place.lng
      },
      addressComponents: place.address_components
    });
    
    setErrors({ ...errors, location: '' });
  };

  const validateStep1 = () => {
    const newErrors = { name: '', location: '' };
    let isValid = true;

    if (!projectData.name.trim()) {
      newErrors.name = 'Project name is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateStep2 = () => {
    const newErrors = { ...errors, location: '' };
    let isValid = true;

    if (!projectData.location.trim()) {
      newErrors.location = 'Please select a valid address';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = () => {
    if (!validateStep2()) {
      return;
    }
    
    // Format the data for the backend
    const formattedData = {
      name: projectData.name,
      location: projectData.location,
      coordinates: projectData.coordinates,
      addressComponents: projectData.addressComponents,
      businessType: projectData.businessType,
      locationType: projectData.locationType
    };
    
    console.log('Submitting project data:', formattedData);
    
    onAdd(formattedData);
  };

  const getSearchTypeForLocationType = () => {
    return projectData.locationType === 'existing' ? 'establishments' : 'all';
  };

  const getLocationPlaceholder = () => {
    return projectData.locationType === 'existing' 
      ? 'Search for existing business (e.g., "Target", "McDonald\'s")'
      : 'Enter address for new location';
  };

  const getLocationLabel = () => {
    return projectData.locationType === 'existing'
      ? 'Search Existing Business'
      : 'Enter New Location Address';
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        width: '100%',
        maxWidth: '500px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h3 style={{ marginBottom: '1.5rem', color: '#1e1e1e' }}>
          Add New Project {step === 2 && '- Location Details'}
        </h3>
        
        {step === 1 && (
          <div>
            {/* Project Name */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="projectName" style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                color: '#333',
                fontWeight: 500
              }}>
                Project Name <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                id="projectName"
                type="text"
                placeholder="Enter project name"
                value={projectData.name}
                onChange={(e) => setProjectData({...projectData, name: e.target.value})}
                style={{ 
                  width: '100%', 
                  padding: '0.75rem', 
                  border: `1px solid ${errors.name ? 'red' : '#ccc'}`, 
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
              />
              {errors.name && (
                <div style={{ color: 'red', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  {errors.name}
                </div>
              )}
            </div>

            {/* Business Type */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="businessType" style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                color: '#333',
                fontWeight: 500
              }}>
                Business Type <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                id="businessType"
                value={projectData.businessType}
                onChange={(e) => setProjectData({...projectData, businessType: e.target.value})}
                style={{ 
                  width: '100%', 
                  padding: '0.75rem', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  fontSize: '1rem',
                  backgroundColor: 'white'
                }}
              >
                {BUSINESS_TYPE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Location Type */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                color: '#333',
                fontWeight: 500
              }}>
                Location Type <span style={{ color: 'red' }}>*</span>
              </label>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="locationType"
                    value="new"
                    checked={projectData.locationType === 'new'}
                    onChange={(e) => setProjectData({...projectData, locationType: e.target.value as 'new' | 'existing'})}
                    style={{ marginRight: '0.5rem' }}
                  />
                  New Location
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="locationType"
                    value="existing"
                    checked={projectData.locationType === 'existing'}
                    onChange={(e) => setProjectData({...projectData, locationType: e.target.value as 'new' | 'existing'})}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Existing Business
                </label>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                {projectData.locationType === 'new' 
                  ? 'Choose this if you\'re opening a new business at a specific address'
                  : 'Choose this if you want to analyze an existing business location'
                }
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            {/* Location Search */}
            <div style={{ marginBottom: '1rem' }}>
              <AddressAutocomplete
                onSelect={handleAddressSelect}
                label={getLocationLabel()}
                placeholder={getLocationPlaceholder()}
                required={true}
                searchType={getSearchTypeForLocationType()}
                prioritizeBusinesses={projectData.locationType === 'existing'}
              />
              {errors.location && (
                <div style={{ color: 'red', fontSize: '0.875rem', marginTop: '-0.5rem' }}>
                  {errors.location}
                </div>
              )}
            </div>

            {/* Selected Location Preview */}
            {projectData.location && (
              <div style={{ 
                marginBottom: '1rem',
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.25rem' }}>
                  Selected Location:
                </div>
                <div style={{ fontSize: '1rem', color: '#333' }}>
                  {projectData.location}
                </div>
                {projectData.coordinates && (
                  <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.25rem' }}>
                    Coordinates: {projectData.coordinates.latitude.toFixed(6)}, {projectData.coordinates.longitude.toFixed(6)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
          {step === 2 && (
            <Button
              onClick={handleBack}
              style={{ 
                backgroundColor: 'white',
                color: '#666',
                border: '1px solid #ccc'
              }}
            >
              Back
            </Button>
          )}
          <Button
            onClick={onClose}
            style={{ 
              backgroundColor: 'white',
              color: '#1e1e1e',
              border: '1px solid #1e1e1e'
            }}
          >
            Cancel
          </Button>
          {step === 1 ? (
            <Button
              onClick={handleNext}
              style={{ 
                backgroundColor: '#7FE7D9',
                color: '#1e1e1e'
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              style={{ 
                backgroundColor: '#7FE7D9',
                color: '#1e1e1e'
              }}
            >
              Add Project
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddProjectModal;
